/**
 * Deeply clone a plain object or array.
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Deep merge two objects. Arrays are replaced, not merged.
 */
export function deepMerge<T extends Record<string, unknown>>(
  target: T,
  source: Partial<T>
): T {
  const result = { ...target };
  for (const key in source) {
    const srcVal = source[key];
    const tgtVal = result[key];
    if (isPlainObject(srcVal) && isPlainObject(tgtVal)) {
      result[key] = deepMerge(
        tgtVal as Record<string, unknown>,
        srcVal as Record<string, unknown>
      ) as T[typeof key];
    } else if (srcVal !== undefined) {
      result[key] = srcVal as T[typeof key];
    }
  }
  return result;
}

/**
 * Pick specific keys from an object.
 * @example pick({ a:1, b:2, c:3 }, ['a','c']) // { a:1, c:3 }
 */
export function pick<T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> {
  return keys.reduce((acc, key) => {
    if (key in obj) acc[key] = obj[key];
    return acc;
  }, {} as Pick<T, K>);
}

/**
 * Omit specific keys from an object.
 * @example omit({ a:1, b:2, c:3 }, ['b']) // { a:1, c:3 }
 */
export function omit<T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> {
  const keysSet = new Set(keys as string[]);
  return Object.fromEntries(
    Object.entries(obj).filter(([k]) => !keysSet.has(k))
  ) as Omit<T, K>;
}

/**
 * Flatten a nested object with dot-notation keys.
 * @example flattenObject({ a: { b: { c: 1 } } }) // { 'a.b.c': 1 }
 */
export function flattenObject(
  obj: Record<string, unknown>,
  prefix = ""
): Record<string, unknown> {
  return Object.entries(obj).reduce<Record<string, unknown>>((acc, [key, val]) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (isPlainObject(val)) {
      Object.assign(acc, flattenObject(val as Record<string, unknown>, fullKey));
    } else {
      acc[fullKey] = val;
    }
    return acc;
  }, {});
}

/**
 * Check if a value is a plain object (not array, not null, not class instance).
 */
export function isPlainObject(val: unknown): val is Record<string, unknown> {
  return typeof val === "object" && val !== null && !Array.isArray(val) &&
    Object.getPrototypeOf(val) === Object.prototype;
}

/**
 * Check if an object is empty (no own enumerable keys).
 */
export function isEmpty(obj: Record<string, unknown>): boolean {
  return Object.keys(obj).length === 0;
}

/**
 * Invert an object's keys and values.
 * @example invertObject({ a: '1', b: '2' }) // { '1': 'a', '2': 'b' }
 */
export function invertObject(
  obj: Record<string, string>
): Record<string, string> {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [v, k]));
}

/**
 * Get a nested value by dot-notation path.
 * @example getByPath({ a: { b: 42 } }, 'a.b') // 42
 */
export function getByPath(obj: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (isPlainObject(acc)) return acc[key];
    return undefined;
  }, obj);
}
